import React, { useEffect, useState } from "react";
import Lottie from "lottie-react";
import {
  FormControl,
  Select,
  MenuItem,
  TextField,
  Box,
  Grid,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import SearchIcon from "@mui/icons-material/Search";

import LottieError from "../../../components/LottieError";
import ProductCard from "./ProductCard";
import axios from "../../../network";
import SEARCH_LOTTIE from "../../../assets/search_lottie.json";
import SnackbarMessage from "../../../components/SnackbarMessage";
import ItemNotFound from "../../../utility/ItemNotFound";
import { useWarehouse } from "../../../contexts/WarehouseContext";

const Search = () => {
  const [selectedOption, setSelectedOption] = useState("product");
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const { warehouseId } = useWarehouse();

  let params = {
    warehouseId: warehouseId,
    word: searchValue,
  };
  useEffect(() => {
    axios.get("categories/getCategories").then((data) => {
      setCategoryList(data.data.data);
    });
  }, [warehouseId]);
  const updatedCategoryList = categoryList.map((category) => ({
    categoryId: category.categoryId,
    label: category.categoryName,
  }));

  useEffect(() => {
    if (selectedOption === "product" && searchValue.length > 0) {
      axios
        .get("skunits/getSkusBySearch/1", { params })
        .then((data) => {
          setSearchResults(data?.data || []);
          setLoading(false);
          setError(false);
        })
        .catch((err) => {
          setLoading(false);
          setError(true);
        });
    }
    if (selectedOption === "category" && searchValue.length > 0) {
      let categoryId = updatedCategoryList.find(
        (category) => category.label === searchValue
      ) || {};
      const params = {
          categoryid: categoryId?.categoryId || 0,
          fetchAllDetails: 1,
          warehouseId: warehouseId,
        };

      axios
        .get("skunits/getallskusbusinessunits/",{ params })
        .then((data) => {
          setSearchResults(data?.data?.Products || []);
          setLoading(false);
          setError(false);

        })
        .catch((err) => {
          setLoading(false);
          setError(true);
        });
    }
  }, [searchValue, selectedOption, warehouseId]);

  const handleOptionChange = (e) => {
    setSelectedOption(e.target.value);
    setSearchValue(""); // Reset search value when the option changes
    setSearchResults([]);
    setLoading(false);
    setError(false);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (value && value !== searchValue) {
      setLoading(true);
    }
    setSearchValue(value);
  };
  const handleOpenSnackBar = (message, severity) => {
    setShowSnackBar(true);
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
  };

  const handleCloseSnackBar = () => {
    setShowSnackBar(false);
  };
  const handleEnterKey = (event) => {
    if (event.key === "Enter") {
      event.target.blur(); // Unfocus the TextField when Enter is pressed
    }
  };
 

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          margin: 1,
        }}
      >
        <FormControl sx={{ flex: 1, background: "white" }}>
          <Select
            id="select-option"
            value={selectedOption}
            onChange={handleOptionChange}
            size="small"
            sx={{
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }}
          >
            <MenuItem value="product">Product</MenuItem>
            <MenuItem value="category">Category</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flex: 2, background: "white" }}>
          <Autocomplete
            id="free-solo"
            freeSolo
            options={selectedOption === "product" ? [] : updatedCategoryList}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                value={searchValue}
                onBlur={handleSearchChange}
                label={
                  <Box sx={{ display: "flex", flexDirection: "row" }}>
                    <SearchIcon />
                    <Box sx={{ marginBottom: 1, marginLeft: 1 }}>
                      {`Search by ${selectedOption}`}
                    </Box>
                  </Box>
                }
                onKeyDown={handleEnterKey}
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  },
                }}
              />
            )}
          />
        </Box>
      </Box>
      {!loading && searchResults.length === 0 && searchValue.length === 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            
          }}
        >
          <Lottie animationData={SEARCH_LOTTIE} style={{ width: "80%" }} />
        </Box>
      )}

      {loading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: 5,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {error && (
        <LottieError/>
      )}
      
      {searchValue.length > 0 && searchResults.length === 0 && !loading && !error && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "80% ",
          }}
        >
          <ItemNotFound />
        </Box>
      )}

      {searchResults.length > 0 && (
        <Grid container spacing={1}>
          {searchResults.map((item) => (
            <ProductCard
              item={item}
              setInventoryItems={setSearchResults}
              key={item.id}
              openSnackBar={handleOpenSnackBar}
            />
          ))}
        </Grid>
      )}
      <SnackbarMessage
        open={showSnackBar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleCloseSnackBar}
      />
    </Box>
  );
};

export default Search;
